import { dateTimeNoSeconds, warsawParts } from '../../shared/dateTime';
import { plNumber } from '../../shared/number';
import { vehicleTypeLabel } from './vehicleTypes';

/**
 * Historia zmian pojazdu (12.10.2026) — nazwy pól, źródła i zapis wartości
 * dla operatora. Kształt jak `CarChange` w API (`apps/api/src/cars/
 * car-changes.ts`): pola i źródła to dane z bazy — podpisujemy je, nie
 * zmieniamy. Pole spoza mapy zostaje jak jest.
 */

export interface CarChangeRow {
  id: number;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  source: string;
  reason: string | null;
  changedAt: string;
  changedByLogin: string | null;
}

type Formatter = (value: string) => string;

const yesNo: Formatter = (v) => (v === 'true' ? 'tak' : v === 'false' ? 'nie' : v);
const moment: Formatter = (v) => {
  const text = dateTimeNoSeconds(v);
  return text === '—' ? v : text;
};
const kg: Formatter = (v) => {
  const n = Number(v);
  return v.trim() !== '' && Number.isFinite(n) ? `${plNumber(n, 0)} kg` : v;
};

const FIELDS: Record<string, { label: string; format?: Formatter }> = {
  // Skład zestawu widziany od tego auta: numery po przecinku, pusto = sam.
  trailer: { label: 'Zestaw (drugi pojazd)' },
  operational: { label: 'W eksploatacji', format: yesNo },
  available: { label: 'Dostępny', format: yesNo },
  unavailableStatus: { label: 'Powód niedostępności' },
  unavailableTo: { label: 'Niedostępny do', format: moment },
  registrationNumber: { label: 'Numer rejestracyjny' },
  carType: { label: 'Typ pojazdu', format: (v) => vehicleTypeLabel(v) || v },
  permissibleLoadCapacity: { label: 'Ładowność', format: kg },
  totalWeightKg: { label: 'Masa całkowita', format: kg },
  numberOfAxles: { label: 'Liczba osi' },
  forklift: { label: 'Wózek', format: yesNo },
  company: { label: 'Spółka' },
  ftlCaretaker: { label: 'Opiekun FTL' },
  // Numer kierowcy w TMS — nazwiska w historii nie trzymamy.
  driver: { label: 'Kierowca (nr w TMS)' },
  adr: { label: 'ADR (wpis ręczny)', format: yesNo },
  // Korekty kartoteki w aplikacji (22.10.2026, `fleetCorrections`) — wartości
  // słowami z API: kierowca numerem w TMS, naczepa numerem, termin z nazwą.
  driverCorrection: { label: 'Kierowca — korekta w aplikacji' },
  trailerCorrection: { label: 'Naczepa — korekta w aplikacji' },
  eligibilityCorrection: { label: 'Termin uprawnienia — korekta w aplikacji' },
};

export function carChangeFieldLabel(field: string): string {
  return FIELDS[field]?.label ?? field;
}

/** Wartość przed/po; brak = zależnie od pola („sam” przy zestawie), inaczej „—”. */
export function carChangeValueText(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return field === 'trailer' ? 'bez drugiego pojazdu' : '—';
  }
  const text = String(value);
  const format = FIELDS[field]?.format;
  return format ? format(text) : text;
}

const SOURCES: Record<string, string> = {
  IMPORT_INCREMENTAL: 'TMS — odczyt zmian (co godzinę)',
  IMPORT_FULL: 'TMS — pełny odczyt nocny',
  MANUAL: 'ręcznie',
};

export function carChangeSourceLabel(source: string): string {
  return SOURCES[source] ?? source;
}

/** Pole wpisu alertu w dzienniku zlecenia — dane, nie tłumaczymy. */
export const FIELD_VEHICLE_CHANGE_ALERT = 'vehicleChangedAfterAssignment';

interface VehicleAlertChange {
  field: string;
  from: unknown;
  to: unknown;
}

interface VehicleChangeAlert {
  carId: number | null;
  registrationNumber: string | null;
  role: 'assigned' | 'variant' | null;
  changes: VehicleAlertChange[];
}

/**
 * JSON wpisu `vehicleChangedAfterAssignment`; `null`, gdy to nie obiekt
 * z listą `changes`. Lustro `parseVehicleChangeAlert` w API
 * (`apps/api/src/user-offers/vehicle-change-alerts.ts`): zmiany bez nazwy
 * pola pomijamy, nieznana rola — bez dopisku.
 */
function parseVehicleChangeAlert(value: string | null | undefined): VehicleChangeAlert | null {
  if (!value || !value.trim().startsWith('{')) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(value);
  } catch {
    return null;
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.changes)) return null;
  const changes: VehicleAlertChange[] = [];
  for (const c of o.changes as unknown[]) {
    if (typeof c !== 'object' || c === null) continue;
    const cc = c as Record<string, unknown>;
    if (typeof cc.field !== 'string') continue;
    changes.push({ field: cc.field, from: cc.from ?? null, to: cc.to ?? null });
  }
  const reg = typeof o.registrationNumber === 'string' ? o.registrationNumber.trim() : '';
  return {
    carId: Number.isInteger(o.carId) ? (o.carId as number) : null,
    registrationNumber: reg === '' ? null : reg,
    role: o.role === 'assigned' || o.role === 'variant' ? o.role : null,
    changes,
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Chwila ISO po polsku: `14.10.2026 12:00`, jak w zdaniu z API. */
function momentText(v: unknown): string | null {
  if (typeof v !== 'string' || v === '') return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const w = warsawParts(d);
  return `${pad(w.day)}.${pad(w.month)}.${w.year} ${pad(w.hour)}:${pad(w.minute)}`;
}

/** Wartość pola do zdania: brak, tak/nie, liczba po polsku, reszta tekstem. */
export function vehicleChangeValue(value: unknown): string {
  if (value === null || value === undefined) return 'brak';
  if (typeof value === 'string') return value.trim() === '' ? 'brak' : value;
  if (typeof value === 'boolean') return value ? 'tak' : 'nie';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : plNumber(value, 2);
  return JSON.stringify(value);
}

const CONTEXT_FIELDS = ['unavailableStatus', 'unavailableTo'];

const setValueText = (v: unknown): string =>
  typeof v === 'string' && v.trim() !== '' ? v : 'bez drugiego pojazdu';

/**
 * Podpisy pól w zdaniu ogólnym — te same co `FIELD_LABELS` w API (zgodność
 * pilnuje test, czytając plik API). Pole spoza listy idzie nazwą z importu.
 */
export const ALERT_FIELD_LABELS: Readonly<Record<string, string>> = {
  trailer: 'zestaw',
  operational: 'w eksploatacji',
  available: 'dostępny',
  unavailableStatus: 'powód niedostępności',
  unavailableTo: 'niedostępny do',
  registrationNumber: 'numer rejestracyjny',
  carType: 'typ pojazdu',
  permissibleLoadCapacity: 'ładowność',
  totalWeightKg: 'masa całkowita',
  numberOfAxles: 'liczba osi',
  forklift: 'wózek',
  company: 'spółka',
  ftlCaretaker: 'opiekun FTL',
  driver: 'kierowca (nr w TMS)',
};

function genericSentence(c: VehicleAlertChange): string {
  const label = ALERT_FIELD_LABELS[c.field] ?? c.field;
  const value = (v: unknown) => (c.field === 'unavailableTo' ? (momentText(v) ?? 'brak') : vehicleChangeValue(v));
  return `Zmieniono w TMS pole „${label}”: ${value(c.from)} → ${value(c.to)}.`;
}

/**
 * Zdanie alertu o zmianie pojazdu — to samo, które API daje w `text`
 * banera i dzwonka (`vehicleChangeAlertText` w `vehicle-change-alerts.ts`).
 * Tu do historii zmian zlecenia i do wpisu, który przyszedł surowy (JSON
 * z API sprzed zmiany). Wpis, który nie jest takim JSON-em, zostaje jak był.
 */
export function vehicleChangeAlertText(value: string | null | undefined): string {
  const a = parseVehicleChangeAlert(value);
  if (!a) return value ?? '';
  const plate = a.registrationNumber ?? (a.carId !== null ? `#${a.carId}` : null);
  const role = a.role === 'assigned' ? 'obsadzony' : a.role === 'variant' ? 'wariant' : null;
  const subject = `Pojazd${plate ? ` ${plate}` : ''}${role ? ` (${role})` : ''}`;
  const byField = new Map(a.changes.map((c) => [c.field, c]));
  const unavailable = byField.get('available')?.to === false;
  const sentences: string[] = [];
  for (const c of a.changes) {
    if (c.field === 'trailer') {
      sentences.push(`Zmienił się zestaw: był ${setValueText(c.from)}, jest ${setValueText(c.to)}.`);
    } else if (c.field === 'operational' && c.to === false) {
      sentences.push('Wyłączony z eksploatacji w TMS.');
    } else if (c.field === 'operational' && c.to === true) {
      sentences.push('Wrócił do eksploatacji w TMS.');
    } else if (c.field === 'available' && c.to === false) {
      const status = byField.get('unavailableStatus')?.to;
      const until = momentText(byField.get('unavailableTo')?.to);
      const details = [
        typeof status === 'string' && status !== '' ? `powód: ${status}` : null,
        until ? `do ${until}` : null,
      ].filter((s): s is string => s !== null);
      sentences.push(`Niedostępny w TMS${details.length > 0 ? ` (${details.join(', ')})` : ''}.`);
    } else if (c.field === 'available' && c.to === true) {
      sentences.push('Znów dostępny w TMS.');
    } else if (!(unavailable && CONTEXT_FIELDS.includes(c.field))) {
      // Kontekst niedostępności jest już w zdaniu wyżej.
      sentences.push(genericSentence(c));
    }
  }
  if (sentences.length === 0) sentences.push('Zmiana w kartotece TMS.');
  return `${subject}: ${sentences.join(' ')}`;
}
